import { supabase } from "@/integrations/supabase/client";

/**
 * Generates a URL-safe slug from text
 */
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[^a-z0-9]+/g, "-")
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, "")
    // Replace multiple consecutive hyphens with single hyphen
    .replace(/-+/g, "-");
};

/**
 * Validates slug format
 */
export const isValidSlugFormat = (slug: string): { valid: boolean; error?: string } => {
  if (!slug) {
    return { valid: false, error: "Slug is required" };
  }

  if (slug.length < 3) {
    return { valid: false, error: "Slug must be at least 3 characters" };
  }

  if (slug.length > 100) {
    return { valid: false, error: "Slug must be less than 100 characters" };
  }

  // Check if slug contains only lowercase letters, numbers, and hyphens
  if (!/^[a-z0-9-]+$/.test(slug)) {
    return { valid: false, error: "Slug can only contain lowercase letters, numbers, and hyphens" };
  }

  // Check if slug starts or ends with hyphen
  if (slug.startsWith("-") || slug.endsWith("-")) {
    return { valid: false, error: "Slug cannot start or end with a hyphen" };
  }

  // Check for consecutive hyphens
  if (slug.includes("--")) {
    return { valid: false, error: "Slug cannot contain consecutive hyphens" };
  }

  // Reserved slugs
  const reservedSlugs = [
    "admin",
    "api",
    "auth",
    "dashboard",
    "settings",
    "analytics",
    "new",
    "edit",
    "delete",
    "login",
    "signup",
    "logout",
    "profile",
    "account",
    "user", // Prevent default "User" company name conflicts
  ];

  if (reservedSlugs.includes(slug)) {
    return { valid: false, error: "This slug is reserved and cannot be used" };
  }

  return { valid: true };
};

/**
 * Checks if slug is unique for the user
 */
export const isSlugUnique = async (slug: string, userId: string, excludePageId?: string): Promise<{ unique: boolean; error?: string }> => {
  try {
    let query = supabase
      .from("feedback_pages")
      .select("id, slug")
      .eq("slug", slug)
      .eq("user_id", userId);

    if (excludePageId) {
      query = query.neq("id", excludePageId);
    }

    const { data, error } = await query;

    if (error) throw error;

    if (data && data.length > 0) {
      return { unique: false, error: "This slug is already in use" };
    }

    return { unique: true };
  } catch (error: any) {
    return { unique: false, error: error.message };
  }
};

/**
 * Generates a unique slug by appending numbers if needed
 */
export const generateUniqueSlug = async (baseSlug: string, userId: string): Promise<string> => {
  let slug = generateSlug(baseSlug);
  let counter = 1;
  
  // Check base slug first
  const { unique } = await isSlugUnique(slug, userId);
  if (unique) {
    return slug;
  }

  // Append numbers until we find a unique slug
  while (counter < 100) {
    const candidateSlug = `${slug}-${counter}`;
    const { unique } = await isSlugUnique(candidateSlug, userId);
    
    if (unique) {
      return candidateSlug;
    }
    
    counter++;
  }

  // If we still can't find a unique slug, append timestamp
  return `${slug}-${Date.now()}`;
};

/**
 * Full slug validation including format and uniqueness
 */
export const validateSlug = async (
  slug: string,
  userId: string,
  excludePageId?: string
): Promise<{ valid: boolean; error?: string }> => {
  // First check format
  const formatCheck = isValidSlugFormat(slug);
  if (!formatCheck.valid) {
    return formatCheck;
  }

  // Then check uniqueness
  const uniqueCheck = await isSlugUnique(slug, userId, excludePageId);
  if (!uniqueCheck.unique) {
    return { valid: false, error: uniqueCheck.error };
  }

  return { valid: true };
};
